import { useEffect, useRef, useState } from 'react';
import type { TeamJwt } from '../auth/teamJwt';
import {
  disablePublicGraph,
  enablePublicGraph,
  getPublicGraphSettings,
  publicWikiPath,
  suggestPublicSlug,
  type PublicGraphSettings,
} from './publicGraphApi';

const VISIBLE_LABELS: Record<string, string> = {
  'wiki-pages': 'Every wiki page: entities, questions, findings, claims, investigations, specs and decisions',
  'page-text': 'The text of those pages',
  'page-fields': 'Their fields, statuses and the links between them',
  labels: 'Labels',
  'types-and-predicates': 'Page types and the predicate vocabulary',
};

const HIDDEN_LABELS: Record<string, string> = {
  comments: 'Comments',
  'authors-and-activity': 'Who wrote or edited anything, and all activity',
  'health-and-proposals': 'Content health and ontology proposals',
  'sessions-and-changesets': 'Sessions and changesets',
  'other-tracker-types': 'Every other tracker (bugs, tasks, plans and the rest)',
  'archived-pages': 'Archived pages',
};

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,62}[a-z0-9])$/;

function label(map: Record<string, string>, id: string): string {
  return map[id] ?? id.replace(/-/g, ' ');
}

/** Loads the project's public-graph state for a member; null while loading or when it cannot be read. */
export function usePublicGraphSettings(
  getTeamJwt: () => Promise<TeamJwt>,
  orgId: string,
  teamProjectId: string | null,
): [PublicGraphSettings | null, (next: PublicGraphSettings) => void, Error | null] {
  const [settings, setSettings] = useState<PublicGraphSettings | null>(null);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!teamProjectId) return;
    const controller = new AbortController();
    setSettings(null);
    setError(null);
    void getTeamJwt()
      .then((jwt) => getPublicGraphSettings(jwt, orgId, teamProjectId, controller.signal))
      .then((next) => { if (!controller.signal.aborted) setSettings(next); })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause : new Error(String(cause)));
      });
    return () => controller.abort();
  }, [getTeamJwt, orgId, teamProjectId]);
  return [settings, setSettings, error];
}

/**
 * Project setup's Public tab: whether the project's knowledge graph can be
 * read by anyone, and for admins the switch. Turning it on goes through a
 * dialog that names what becomes visible; turning it off is immediate.
 */
export function PublicGraphSettingsPanel({ orgId, teamProjectId, projectName, isAdmin, getTeamJwt }: {
  orgId: string;
  teamProjectId: string;
  projectName: string;
  isAdmin: boolean;
  getTeamJwt: () => Promise<TeamJwt>;
}) {
  const [settings, setSettings, loadError] = usePublicGraphSettings(getTeamJwt, orgId, teamProjectId);
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (settings) setSlug(settings.slug ?? suggestPublicSlug(projectName));
  }, [settings, projectName]);

  if (loadError) return <p className="public-graph-settings m-0 p-6 text-[13px] text-nim-error" role="alert">{loadError.message}</p>;
  if (!settings) return <p className="public-graph-settings m-0 p-6 text-[13px] text-nim-faint">Loading…</p>;

  const url = settings.public && settings.slug ? `${window.location.origin}${publicWikiPath(settings.slug)}` : null;
  const slugValid = SLUG_PATTERN.test(slug) && !slug.includes('--');

  const run = async (action: (jwt: TeamJwt) => Promise<PublicGraphSettings>) => {
    setBusy(true);
    setError(null);
    try {
      setSettings(await action(await getTeamJwt()));
      dialog.current?.close();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="public-graph-settings flex max-w-[720px] flex-col gap-4 p-6 text-[13px] text-nim">
      <div className="flex flex-col gap-1">
        <h2 className="m-0 text-[15px] font-semibold">Public wiki</h2>
        <p className="m-0 text-nim-muted">
          {url
            ? <>Anyone with the link can read this project’s wiki, without signing in: <a className="text-nim-link" href={url} target="_blank" rel="noreferrer">{url}</a></>
            : 'Only members of this organization can read this project’s wiki.'}
        </p>
      </div>

      {!isAdmin && <p className="m-0 text-nim-faint">Only organization admins can change this.</p>}

      {isAdmin && settings.public && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="public-graph-disable cursor-pointer rounded border border-nim bg-transparent px-3 py-1.5 text-nim disabled:opacity-60"
            disabled={busy}
            onClick={() => { void run((jwt) => disablePublicGraph(jwt, orgId, teamProjectId)); }}
          >
            {busy ? 'Making private…' : 'Make private'}
          </button>
          <span className="text-nim-faint">Takes effect immediately.</span>
        </div>
      )}

      {isAdmin && !settings.public && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-nim-muted">Public address</span>
            <span className="flex items-center gap-1 text-nim-faint">
              {window.location.origin}/public/wiki/
              <input
                className="public-graph-slug rounded border border-nim bg-nim px-2 py-1 text-nim"
                value={slug}
                onChange={(event) => setSlug(event.target.value.toLowerCase())}
                aria-invalid={!slugValid}
              />
            </span>
          </label>
          <button
            type="button"
            className="public-graph-enable cursor-pointer rounded border border-nim bg-transparent px-3 py-1.5 text-nim disabled:opacity-60"
            disabled={!slugValid}
            onClick={() => { setError(null); dialog.current?.showModal(); }}
          >
            Make public…
          </button>
          {!slugValid && <span className="text-nim-faint">3–64 lowercase letters, digits and single hyphens.</span>}
        </div>
      )}

      {error && <p className="m-0 text-nim-error" role="alert">{error}</p>}

      <dialog
        ref={dialog}
        className="public-graph-confirm max-w-[520px] rounded-lg border border-nim bg-nim p-5 text-[13px] text-nim"
        aria-labelledby="public-graph-confirm-title"
      >
        <h3 id="public-graph-confirm-title" className="m-0 mb-2 text-[15px] font-semibold">Make this wiki public?</h3>
        <p className="m-0 mb-2 text-nim-muted">Anyone with the link, and search engines, will be able to read:</p>
        <ul className="m-0 mb-3 flex flex-col gap-1 pl-5">
          {settings.visible.map((id) => <li key={id}>{label(VISIBLE_LABELS, id)}</li>)}
        </ul>
        <p className="m-0 mb-2 text-nim-muted">Still private:</p>
        <ul className="m-0 mb-3 flex flex-col gap-1 pl-5 text-nim-muted">
          {settings.hidden.map((id) => <li key={id}>{label(HIDDEN_LABELS, id)}</li>)}
        </ul>
        <p className="m-0 mb-4 text-nim-muted">A link from a public page to a private item shows only that item’s title.</p>
        {error && <p className="m-0 mb-3 text-nim-error" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="cursor-pointer rounded border border-nim bg-transparent px-3 py-1.5 text-nim" onClick={() => dialog.current?.close()}>
            Cancel
          </button>
          <button
            type="button"
            className="public-graph-confirm-button cursor-pointer rounded border-none bg-[var(--nim-primary)] px-3 py-1.5 text-white disabled:opacity-60"
            disabled={busy}
            onClick={() => { void run((jwt) => enablePublicGraph(jwt, orgId, teamProjectId, slug, settings.visible)); }}
          >
            {busy ? 'Making public…' : 'Make public'}
          </button>
        </div>
      </dialog>
    </section>
  );
}
